import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useEffect, useState } from 'react';

import { Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon, InfoCircle } from 'chatie-design-system/icon';

import { useBadgePresets } from '@components/common/Badge';
import CurationTab from '@components/common/CurationTab';
import SafeArea from '@components/layout/SafeArea';
import Divider from '@components/outlook/Divider';
import RRViewList from '@components/stories/RRViewList';
import RSViewList from '@components/stories/RSViewList';
import SRViewList from '@components/stories/SRViewList';
import WFCPViewList from '@components/stories/WFViewList';

import { CurationViewType } from '@constants';
import { IRecommendSection, IStoryAd } from '@model';
import { media } from '@styles/css';
import { ParsedStorage } from '@utils/localStorage';
import { useWebView } from '@utils/webview';

const storyCurationCSS = (hasChildren: boolean) => (theme: Theme) => css`
  position: relative;
  padding: ${Spacing.medium}px ${Spacing.medium}px 0px;
  margin-bottom: ${hasChildren
    ? ` ${Spacing.small}px;`
    : `${Spacing.xxsmall}px`} !important;

  ${media.tablet} {
    padding: ${Spacing.medium}px ${Spacing.xlarge}px 0px;
  }
  ${media.desktop} {
    padding: ${Spacing.medium}px ${Spacing.xxlarge}px 0px;
  }

  .title {
    overflow: hidden;
    ${Typography.h5};
    text-overflow: ellipsis;
    color: ${theme.var.black};
  }

  .more-container {
    display: flex;
    flex-direction: row;
    ${Typography.body14};
    align-items: center;
    min-width: 53px;
  }

  .more {
    color: ${theme.var.gray400};
  }

  .header-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow: hidden;
    align-items: center;
    text-align: center;
    height: 16px;
  }
  .title-container {
    text-iverflow: ellipsis;
    overflow: hidden;
  }
  .left-container {
    display: flex;
    flex-direction: row;
    align-items: center;

    .info-btn-container {
      width: 16px;
      margin-left: ${Spacing.xsmall}px;
    }
  }
`;

const contentWrapCSS = css`
  padding-bottom: ${Spacing.medium}px;

  ${media.tablet} {
    padding-bottom: ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding-bottom: ${Spacing.xxlarge}px;
  }
`;

const cpCSS = (theme: Theme) => css`
  background: ${
    theme.name === 'dark'
      ? theme.var.gray50
      : `linear-gradient(190deg, transparent 35.5%, ${theme.colors.main500} 36%)`
  };

  .gridContainer {
    padding-bottom: ${Spacing.medium}px !important;
  }
  article > div:first-of-type {
    border-radius: 4px 4px 0px 0px;
  }
`;
const RcommendationStorySection = ({
  section,
  ad,
  openPopupModal,
  showNativeAd = false,
  showFreeChatCta = false,
}: {
  section: IRecommendSection;
  ad: IStoryAd | undefined;
  openPopupModal: (modalState?: unknown) => void;
  showNativeAd?: boolean;
  showFreeChatCta?: boolean;
}) => {
  const {
    viewType,
    name,
    id,
    moreViewType,
    hasAd,
    hasLine,
    popupInfoContext,
    children,
  } = section;
  const { openPageOrMove } = useWebView();
  const { t } = useTranslation();
  const theme = useTheme();

  const isCPView = viewType === CurationViewType.chatiePick;
  const isWFView = viewType === CurationViewType.waitfree;
  const isRSView = viewType === CurationViewType.recentStory;
  const isRRView = viewType === CurationViewType.realtimeRanking;
  const isSRView = viewType === CurationViewType.storyRanking;

  const hasMore = !!section?.moreViewType;
  const btnInfoEvent = !!popupInfoContext?.title;
  const hasStoryOrChildren = !!section?.stories?.length || !!children?.length;
  const [selctedSectionItem, setSelectedSectionItem] =
    useState<IRecommendSection>(section);
  const { grayFill } = useBadgePresets();

  const handleClickMore = () => {
    const subSections = section?.children?.length
      ? JSON.stringify(
          section?.children?.map((item) => {
            return { id: item.id, name: item.name };
          })
        )
      : '';

    const subSectionIndex = selctedSectionItem
      ? section?.children?.findIndex(
          (item) => item.id === selctedSectionItem?.id
        )
      : 0;
    openPageOrMove(
      `/curations/${id}?title=${name}&view_type=${moreViewType}&sub_sections=${subSections}&sub_section_index=${subSectionIndex}`
    );
  };

  const handleClickChildSection = (section: IRecommendSection) => {
    setSelectedSectionItem(section);
    const prevTab = ParsedStorage.getItem('selectedSectionTab');
    ParsedStorage.setItem('selectedSectionTab', {
      ...prevTab,
      [id]: section?.id,
    });
  };

  useEffect(() => {
    if (!!children?.length) {
      const prevTab = ParsedStorage.getItem('selectedSectionTab');
      const prevTabId = prevTab?.[id];
      const hasPrevTabId =
        !!prevTabId &&
        section?.children?.some((child) => child?.id === prevTabId);
      if (hasPrevTabId) {
        const prevSection = section?.children?.find(
          (child) => child?.id === prevTabId
        );
        setSelectedSectionItem(
          prevSection ? prevSection : section?.children[0]
        );
      } else {
        setSelectedSectionItem(section?.children[0]);
      }
    }
  }, []);

  const handleClickBtnInfo = () => {
    const { title, description, tip } = popupInfoContext;
    openPopupModal({ title: title, description: description, tip: tip });
  };

  const isShowAd = !!ad && !!hasAd && !!ad?.story;

  if (!hasStoryOrChildren) {
    return <></>;
  }

  return (
    <SafeArea key={id}>
      {hasLine && <Divider />}
      <div css={storyCurationCSS(!!children?.length)}>
        <div className="header-container">
          <div className="left-container">
            <div className="title-container">
              <p className="title ellipsis">{name}</p>
            </div>
            {!!btnInfoEvent && (
              <div className="info-btn-container" onClick={handleClickBtnInfo}>
                <Icon
                  icon={InfoCircle}
                  color={theme.colors.main500}
                  size={Sizes.small}
                />
              </div>
            )}
          </div>
          {hasMore && (
            <div onClick={handleClickMore} className="more-container">
              <p className="more">{t('더보기')}</p>
              <Icon icon={ArrowRight} color={theme.var.gray400} size={Sizes.small} />
            </div>
          )}
        </div>
      </div>
      <div css={contentWrapCSS}>
        {!!children?.length && (
          <CurationTab
            sections={children}
            onClickSection={handleClickChildSection}
            selectedSectionId={selctedSectionItem?.id}
          />
        )}
        {isSRView && hasStoryOrChildren && (
          <SRViewList
            curation={selctedSectionItem?.stories}
            isShowAd={isShowAd}
            ad={ad}
            badgePreset={grayFill}
            showNativeAd={showNativeAd}
            showFreeChatCta={showFreeChatCta}
          />
        )}
        {isRRView && hasStoryOrChildren && (
          <RRViewList
            curation={selctedSectionItem?.stories}
            isShowAd={isShowAd}
            ad={ad}
            badgePreset={grayFill}
            onClickMore={handleClickMore}
          />
        )}

        {isWFView && hasStoryOrChildren && (
          <WFCPViewList
            stories={selctedSectionItem?.stories}
            hasAd={hasAd}
            badgePreset={grayFill}
            ad={ad}
            showNativeAd={showNativeAd}
          />
        )}
        {isCPView && hasStoryOrChildren && (
          <div css={cpCSS}>
            <WFCPViewList
              stories={selctedSectionItem?.stories}
              hasAd={hasAd}
              badgePreset={grayFill}
              ad={ad}
              showNativeAd={showNativeAd}
              showFreeChatCta={showFreeChatCta}
            />
          </div>
        )}
        {isRSView && hasStoryOrChildren && (
          <RSViewList
            curation={selctedSectionItem?.stories}
            isShowAd={isShowAd}
            ad={ad}
            badgePreset={grayFill}
          />
        )}
      </div>
      {hasLine && <Divider />}
    </SafeArea>
  );
};

export default RcommendationStorySection;
